import React, { useEffect, useState } from 'react';
import { Card, CardBody, CardHeader } from 'reactstrap';
import UnitDetails from './UnitDetails';
import PropertyDocuments from './PropertyDocuments';
import AdditionalDocuments from './AdditionalDocuments';
import PropertyAmenities from './propertyAmenities';
import Utilities from './Utilities';
import Parking from './Parking';
import Laundry from './Laundry';
import Pets from './Pets';
import Media from './Media';
import Description from './Description';
import Demographics from './Demographics';
import ContactInformation from './ContactInformation';
import { formatDateTime, formatPrice } from 'lib/utils/formatters';
import CurrentOffer from './CurrentOffer';

interface ReviewListingProps {
    propertyDetails?: any;
    isLoading?: boolean;
}

const ReviewListing: React.FC<ReviewListingProps> = ({ propertyDetails, isLoading: _isLoading }) => {

    const [unitDetailsData, setUnitDetailsData] = useState({
        beds: '',
        baths: '',
        sqft: '',
        dateAvailable: '',
        totalPrice: '',
    });

    const [unitDescription, setUnitDescription] = useState('');
    const [propertyDocumentsData, setPropertyDocumentsData] = useState([]);
    const [additionalDocumentsData, setAdditionalDocumentsData] = useState([]);

    const [contactInfoData, setContactInfoData] = useState({
        firstName: '',
        lastName: '',
        isNameVisible: false,
        email: '',
        phone: '',
        contactPreference: '',
    });

    const [currentOfferData, setCurrentOfferData] = useState({
        id: '',
        createdAt: '',
        offer_amount: 0,
        status: '',
        customer: {
            id: '',
            first_name: '',
            last_name: ''
        }
    });

    const [demographicsData, setDemographicsData] = useState({
        transit: [],
        parksRecreation: [],
        education: [],
        foodDrinks: [],
    });

    const [propertyAmenitiesData, setPropertyAmenitiesData] = useState([]);

    const [propertyUtilitiesData, setPropertyUtilitiesData] = useState([]);


    const [parkingFacilities, setParkingFacilities] = useState([]);
    const [laundryFacilities, setLaundryFacilities] = useState([]);
    const [petsFacilities, setPetsFacilities] = useState([]);

    const [mediaData, setMediaData] = useState({
        images: [],
        videos: [],
        virtualTours: [],
    });



    useEffect(() => {
        if (propertyDetails) {
            setUnitDetailsData({
                beds: propertyDetails?.bedrooms || '0',
                baths: propertyDetails?.bath || '0',
                sqft: propertyDetails?.size_sq_ft || '0',
                dateAvailable: formatDateTime(propertyDetails?.available_date),
                totalPrice: formatPrice(propertyDetails?.total_Price),
            });
            setUnitDescription(propertyDetails?.unit_description || '');
            setPropertyDocumentsData(propertyDetails?.propertyDocuments || []);
            setAdditionalDocumentsData(propertyDetails?.additionalDocuments || []);
            setContactInfoData({
                firstName: propertyDetails?.property?.ContactInfo?.first_name || '',
                lastName: propertyDetails?.property?.ContactInfo?.last_name || '',
                isNameVisible: propertyDetails?.property?.ContactInfo?.is_name_visible || false,
                email: propertyDetails?.property?.ContactInfo?.email || '',
                phone: propertyDetails?.property?.ContactInfo?.phone_no || '',
                contactPreference: propertyDetails?.property?.ContactInfo?.contact_preference || '',
            });

            const demographics = propertyDetails?.property?.propertyDemographic || [];
            
            // Helper function for case-insensitive and flexible matching
            const normalizeType = (type: string) => type.toLowerCase().replace(/\s/g, '').replace(/&/g, '');
            
            const transitData = demographics.filter((item: any) => {
                const normalized = normalizeType(item.demographic_type || '');
                return normalized === 'transit';
            });
            
            const parksRecreationData = demographics.filter((item: any) => {
                const normalized = normalizeType(item.demographic_type || '');
                return normalized === 'parksrecreation' || normalized === 'parkrecreation';
            });
            
            const educationData = demographics.filter((item: any) => {
                const normalized = normalizeType(item.demographic_type || '');
                return normalized === 'education';
            });
            
            const foodDrinksData = demographics.filter((item: any) => {
                const normalized = normalizeType(item.demographic_type || '');
                return normalized === 'fooddrink' || normalized === 'fooddrinks';
            });

            setDemographicsData({
                transit: transitData,
                parksRecreation: parksRecreationData,
                education: educationData,
                foodDrinks: foodDrinksData,
            });

            setPropertyAmenitiesData(propertyDetails?.propertyAmenities || []);

            setPropertyUtilitiesData(propertyDetails?.propertyUtilities || []);

            const facilityValues = propertyDetails?.property?.facility_values || [];
            const parkingData = facilityValues.filter((item: any) => item.facility_type === 'parking');
            const laundryData = facilityValues.filter((item: any) => item.facility_type === 'laundry');
            const petsData = facilityValues.filter((item: any) => item.facility_type === 'pets');

            setParkingFacilities(parkingData);
            setLaundryFacilities(laundryData);
            setPetsFacilities(petsData);

            const propertyMedia = propertyDetails?.PropertyMedia || [];

            const imagesData = propertyMedia.filter((item: any) => item.file_type === 'unit_image');
            const videosData = propertyMedia.filter((item: any) => item.file_type === 'unit_video');
            const virtualToursData = propertyMedia.filter((item: any) => item.file_type === 'unit_virtual_tour');

            setMediaData({
                images: imagesData,
                videos: videosData,
                virtualTours: virtualToursData,
            });

            // Set current offer data
            if (propertyDetails?.currentOffer) {
                setCurrentOfferData({
                    id: propertyDetails.currentOffer.id || '',
                    createdAt: propertyDetails.currentOffer.createdAt || '',
                    offer_amount: propertyDetails.currentOffer.offer_amount || 0,
                    status: propertyDetails.currentOffer.status || '',
                    customer: {
                        id: propertyDetails.currentOffer.customer?.id || '',
                        first_name: propertyDetails.currentOffer.customer?.first_name || '',
                        last_name: propertyDetails.currentOffer.customer?.last_name || ''
                    }
                });
            }

        }
    }, [propertyDetails]);

    return (
        <Card className='rounded-0 h-100'>
            <CardHeader className="py-3">
                <h6 className="my-1">Review Listing</h6>
            </CardHeader>
            <CardBody className="d-flex flex-column h-100">

                <div className="shadow p-3 mb-2 bg-light rounded">
                    <h6 className="my-1">Unit Details</h6>
                </div>
                <UnitDetails unitDetailsData={unitDetailsData} />

                <div className="shadow p-3 mb-3 bg-light rounded">
                    <h6 className="my-1">Property Documents</h6>
                </div>
                <PropertyDocuments propertyDocumentsData={propertyDocumentsData} />
                <div className="shadow p-3 mb-2 bg-light rounded">
                    <h6 className="my-1">Additional Documents</h6>
                </div>
                <AdditionalDocuments additionalDocumentsData={additionalDocumentsData} />
                <div className="shadow p-3 mb-2 bg-light rounded">
                    <h6 className="my-1">Property Amenities</h6>
                </div>
                <PropertyAmenities propertyAmenitiesData={propertyAmenitiesData} />
                <div className="shadow p-3 mb-2 bg-light rounded">
                    <h6 className="my-1">Utilities</h6>
                </div>
                <Utilities propertyUtilitiesData={propertyUtilitiesData} />
                <div className="shadow p-3 mb-2 bg-light rounded">
                    <h6 className="my-1">Parking</h6>
                </div>
                <Parking parkingFacilities={parkingFacilities} />
                <div className="shadow p-3 mb-2 bg-light rounded">
                    <h6 className="my-1">Laundry</h6>
                </div>
                <Laundry laundryFacilities={laundryFacilities} />
                <div className="shadow p-3 mb-2 bg-light rounded">
                    <h6 className="my-1">Pets</h6>
                </div>
                <Pets petsFacilities={petsFacilities} />

                <div className="shadow p-3 mb-2 bg-light rounded">
                    <h6 className="my-1">Media</h6>
                </div>
                <Media mediaData={mediaData} />

                {unitDescription && (
                    <>
                        <div className="shadow p-3 mb-3 bg-light rounded">
                            <h6 className="my-1">Description</h6>
                        </div>
                        <Description unitDescription={unitDescription} />
                    </>
                )}

                <div className="shadow p-3 mb-2 bg-light rounded">
                    <h6 className="my-1">Demographics</h6>
                </div>
                <Demographics demographicsData={demographicsData} />

                <div className="shadow p-3 mb-2 bg-light rounded">
                    <h6 className="my-1">Contact Information</h6>
                </div>
                <ContactInformation contactInfoData={contactInfoData} />

                {currentOfferData?.id && (
                    <div className="shadow p-3 mb-2 bg-light rounded">
                        <h6 className="my-1">Current Offer</h6>
                    </div>
                )}
                {currentOfferData?.id && <CurrentOffer currentOfferData={currentOfferData} />}
            </CardBody>
        </Card>
    );
};

export default ReviewListing;   